import React, { useCallback, useEffect, useRef, useState } from "react";
import { Check, X } from "lucide-react";

/**
 * The Supernova tab's toast — the confirmation a member sees after a stake is
 * mined. Scoped to this tab on purpose: there is no app-wide toast system, and
 * SUP-4 is not the place to introduce one into `common/`.
 *
 * It is announced (role="status", aria-live="polite") rather than trapping
 * focus, and it always carries a dismiss control — an auto-hiding message that
 * can't be re-read is a bad way to tell someone about a transaction.
 *
 * COMPLIANCE: the caller supplies the copy. It must stay inside the same floor
 * as the rest of the tab — XP is an in-app points balance, and no return,
 * income, yield, percentage or dollar figure belongs in a toast either.
 */

const DEFAULT_TTL_MS = 10000;

/**
 * @returns {{toast: {id: number, message: string}|null, showToast: (message: string) => void, dismissToast: () => void}}
 */
export function useSupernovaToast() {
  const [toast, setToast] = useState(null);
  const seq = useRef(0);

  const showToast = useCallback((message) => {
    if (!message) return;
    seq.current += 1;
    setToast({ id: seq.current, message });
  }, []);

  const dismissToast = useCallback(() => setToast(null), []);

  return { toast, showToast, dismissToast };
}

const SupernovaToast = ({ toast, onDismiss, ttlMs = DEFAULT_TTL_MS }) => {
  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => onDismiss?.(), ttlMs);
    // Keyed on toast.id so a second toast restarts the clock rather than
    // inheriting the remainder of the first one's.
    return () => clearTimeout(timer);
  }, [toast, ttlMs, onDismiss]);

  if (!toast) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="supernova-toast"
      className="fixed bottom-6 right-6 z-[60] max-w-[380px] flex items-start gap-3 rounded-[14px] bg-arc-card ring-1 ring-arc-line-2 shadow-arc-lg px-4 py-3.5 text-left"
    >
      <Check size={17} className="flex-shrink-0 mt-0.5 text-arc-pos" aria-hidden="true" />
      <p className="text-[13.5px] leading-[1.5] text-arc-ink-2 flex-1">{toast.message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss notification"
        className="-mr-1 -mt-1 p-1.5 rounded-arc-sm text-arc-ink-4 hover:text-arc-ink hover:bg-arc-paper-2 transition"
      >
        <X size={15} aria-hidden="true" />
      </button>
    </div>
  );
};

export default SupernovaToast;
